import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Pagination,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import API from "../../../../api";
import { colors } from "../../../../theme";
import { startToast, updateToastSuccess, updateToastWarning, updateToastError } from "../../../../utils/notification-loading";

// Righe per pagina di una tabella nel tab "Values" — una tabella SNMP tipo
// "software installato" può avere centinaia di righe, paginare invece di
// scrollare all'infinito le tiene leggibili (stile Zabbix/Lansweeper).
const TABLE_ROWS_PER_PAGE = 15;

const countNodes = (nodes) =>
  (nodes || []).reduce((total, node) => total + 1 + countNodes(node.children), 0);

// Nome del nodo + di tutti i suoi discendenti, usato sia per calcolare lo
// stato del checkbox (checked/indeterminate) sia per applicare la selezione
// a cascata quando l'utente clicca un nodo.
const collectNames = (node) => [node.name, ...(node.children || []).flatMap(collectNames)];

// "checked" solo se il nodo e tutti i discendenti sono selezionati,
// "unchecked" se nessuno lo è, "indeterminate" nei casi intermedi (es. dopo
// che l'utente ha escluso un figlio da una selezione a cascata).
const getNodeState = (node, checkedSet) => {
  const names = collectNames(node);
  const checkedCount = names.filter((n) => checkedSet.has(n)).length;
  if (checkedCount === 0) return "unchecked";
  if (checkedCount === names.length) return "checked";
  return "indeterminate";
};

// read-write/read-create sono gli unici accessi che permettono una SET: li
// evidenziamo in ambra, il resto (read-only, not-accessible,
// accessible-for-notify) resta grigio informativo.
const accessColor = (access) => (access === "read-write" || access === "read-create" ? "#f5b642" : colors.grey[500]);

// Solo per l'hover del link "Edit in Configure" nel pannello di dettaglio —
// i valori stessi restano testo semplice, nessun colore addosso.
const LINK_HOVER_COLOR = "primary.main";
// Sfondo alternato (zebra) per le righe delle tabelle — leggibilità su
// molte colonne/righe, discreto (non un colore, solo un filo più chiaro).
const ROW_STRIPE = "#ffffff07";

// Appiattisce l'albero di UN MIB in una Map nome -> nodo (accumulando su una
// mappa condivisa se passata) — usata per recuperare la description di un
// campo nel tab "Values", che mostra i valori combinati di TUTTI i MIB
// caricati per l'asset, non solo di quello attivo nel tab "Configure".
const flattenNodes = (nodes, map = new Map()) => {
  (nodes || []).forEach((node) => {
    map.set(node.name, node);
    flattenNodes(node.children, map);
  });
  return map;
};

// Rende un valore estratto in forma leggibile — le colonne di tabella senza
// correlazione (profili vecchi, senza "table") arrivano come lista di
// valori scalari, le altre come valore singolo. Tronca le liste lunghe per
// non rompere il layout.
// Percorso (nomi, radice -> target incluso) fino al nodo `targetName`
// nell'albero di UN MIB — usato per sapere quali antenati forzare espansi
// quando si salta dal tab Values a un valore nel tab Configure ("Edit in
// Configure" nel pannello di dettaglio), così il nodo è visibile senza dover
// aprire i rami a mano.
const findPathToNode = (nodes, targetName) => {
  for (const node of nodes || []) {
    if (node.name === targetName) return [node.name];
    const childPath = findPathToNode(node.children, targetName);
    if (childPath) return [node.name, ...childPath];
  }
  return null;
};

const formatValue = (value) => {
  if (!Array.isArray(value)) return String(value);
  if (value.length <= 3) return value.join(", ");
  return `${value.slice(0, 3).join(", ")}, … +${value.length - 3}`;
};

// Una tabella SNMP vera e propria (colonne correlate per indice di riga dal
// firmware — vedi SnmpScanner.scan_profile) arriva come lista di OGGETTI,
// una entry per riga: {colonna: valore, ...}. Una lista di valori scalari
// (vecchio formato, colonne non correlate) resta gestita da formatValue.
const isRowTable = (value) =>
  Array.isArray(value) && value.length > 0 && value.every((row) => row && typeof row === "object" && !Array.isArray(row));

const formatCell = (value) => (value === undefined || value === null ? "—" : formatValue(value));

// Chiave univoca di un campo dentro un MIB (file + nome) — identifica quale
// foglia dell'albero valori è selezionata nel tab "Values".
const valueKey = (mibFilename, fieldName) => `${mibFilename}::${fieldName}`;

// Messaggio da appendere al toast di save/delete quando dei campi selezionati
// sono stati esclusi dal push perché il loro OID non si è risolto
// numericamente (import verso un altro MIB non ancora caricato) — così
// l'utente sa SUBITO che quei campi non verranno mai raccolti, invece di
// scoprirlo mesi dopo notando che il tab Values non li mostra mai.
const describeUnresolved = (unresolvedFields) => {
  if (!unresolvedFields?.length) return "";
  const names = unresolvedFields.slice(0, 3).join(", ");
  const extra = unresolvedFields.length > 3 ? `, +${unresolvedFields.length - 3} more` : "";
  return ` — ${unresolvedFields.length} field${unresolvedFields.length === 1 ? "" : "s"} skipped, OID not fully resolved (${names}${extra}): upload the MIB module that defines their parent`;
};

// Un nodo dell'albero MIB: nome + tipo + access + OID (assoluto se la radice
// è un prefisso SMI noto come "enterprises", altrimenti un percorso
// simbolico — vedi mibFieldParser.mjs lato server), espandibile se ha figli.
// Selezionare un nodo seleziona a cascata tutti i suoi discendenti; da lì
// l'utente può escludere singoli figli senza deselezionare il resto. Questo
// è l'albero di CONFIGURAZIONE (cosa raccogliere) del MIB attivo — i valori
// raccolti (combinati su tutti i MIB dell'asset) si vedono nel tab "Values"
// a parte: stessa separazione che software come Zabbix (item config vs.
// Latest data) o Lansweeper (custom fields in tabella pulita) usano tra
// "cosa monitorare" e "cosa è stato letto".
// `forceExpandSet` (nomi da tenere espansi per rivelare un discendente
// puntato dal tab Values) e `highlightName` (il nodo puntato, evidenziato e
// scrollato in vista) sono opzionali — l'albero funziona identico se non
// passati (navigazione manuale normale).
const MibTreeNode = ({ node, checkedSet, onToggle, depth = 0, forceExpandSet, highlightName }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children?.length > 0;
  const state = getNodeState(node, checkedSet);
  const isHighlighted = highlightName === node.name;
  const rowRef = useRef(null);

  useEffect(() => {
    if (forceExpandSet?.has(node.name)) setExpanded(true);
  }, [forceExpandSet, node.name]);

  useEffect(() => {
    if (isHighlighted) rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [isHighlighted]);

  return (
    <Box>
      <Box
        ref={rowRef}
        sx={{
          display: "flex", alignItems: "center", pl: depth * 2.5,
          ...(isHighlighted ? { bgcolor: "#4cceac22", borderRadius: 1, outline: "1px solid #4cceac66" } : {}),
        }}
      >
        {hasChildren ? (
          <IconButton size="small" onClick={() => setExpanded((e) => !e)} sx={{ p: 0.3 }}>
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28, flexShrink: 0 }} />
        )}
        <Checkbox
          size="small"
          checked={state === "checked"}
          indeterminate={state === "indeterminate"}
          onChange={() => onToggle(node)}
        />
        <Tooltip title={node.description || ""} arrow placement="top">
          <Typography sx={{ fontSize: 12.5, color: colors.grey[200], whiteSpace: "nowrap" }}>
            {node.name}
          </Typography>
        </Tooltip>
        <Typography sx={{ fontSize: 10.5, color: colors.grey[500], ml: 1, whiteSpace: "nowrap" }}>
          {node.type || node.kind}
        </Typography>
        {node.access && (
          <Typography sx={{ fontSize: 10.5, color: accessColor(node.access), ml: 1, whiteSpace: "nowrap" }}>
            [{node.access}]
          </Typography>
        )}
        {node.oid && (
          <Tooltip
            title={
              node.oidResolved === false
                ? "This OID couldn't be fully resolved — it references a MIB module that hasn't been uploaded for this asset. Upload it too, or this field won't be collected."
                : ""
            }
            arrow
            placement="top"
          >
            <Typography
              sx={{
                fontSize: 10.5,
                color: node.oidResolved === false ? "#f5b642" : (colors.blueAccent?.[300] || "#6870fa"),
                ml: 1.5,
                fontFamily: "monospace",
              }}
            >
              {node.oid}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {hasChildren && expanded && (
        <Box>
          {node.children.map((child) => (
            <MibTreeNode
              key={child.name}
              node={child}
              checkedSet={checkedSet}
              onToggle={onToggle}
              depth={depth + 1}
              forceExpandSet={forceExpandSet}
              highlightName={highlightName}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Riga dell'albero VALORI (tab "Values") — stessa struttura ad albero del
// MIB del tab Configure ma senza checkbox: le foglie sono i soli campi con
// un valore estratto (scalari o intere tabelle), i rami sono la struttura
// per arrivarci. Aperto di default (come l'header del gruppo MIB sopra di
// esso); durante una ricerca (`isSearching`) resta comunque aperto anche se
// l'utente lo aveva chiuso a mano, per rivelare i match senza doverli
// riaprire — l'albero è già stato potato a monte a contenere solo rami con
// almeno un discendente che corrisponde.
const ValueTreeNode = ({ node, depth = 0, mibFilename, selectedKey, onSelect, isSearching }) => {
  const [expanded, setExpanded] = useState(true);
  const isLeaf = node.children.length === 0;
  const key = valueKey(mibFilename, node.name);
  const isSelected = isLeaf && selectedKey === key;
  const showChildren = isSearching || expanded;

  return (
    <Box>
      <Box
        onClick={isLeaf ? () => onSelect(mibFilename, node) : () => setExpanded((e) => !e)}
        sx={{
          // pl parte da 1.5 (non 0) allo stesso modo dell'header del gruppo
          // MIB (px:1.5 lì sopra): a depth 0 i due testi si allineano
          // esattamente, invece del figlio che risultava più a sinistra del
          // genitore.
          display: "flex", alignItems: "center", gap: 0.5, pl: 1.5 + depth * 1.75, pr: 1, py: 0.5,
          cursor: "pointer", borderRadius: 1,
          ...(isSelected ? { bgcolor: "#45909526" } : { "&:hover": { bgcolor: "#ffffff08" } }),
        }}
      >
        {isLeaf ? (
          <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: isSelected ? "primary.main" : colors.grey[500], flexShrink: 0, ml: 0.5, mr: 0.5 }} />
        ) : showChildren ? (
          <ExpandMoreIcon sx={{ fontSize: 14, color: colors.grey[500] }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 14, color: colors.grey[500] }} />
        )}
        <Typography
          sx={{
            fontSize: 11.5, fontFamily: isLeaf ? "monospace" : undefined, fontWeight: isLeaf ? 400 : 600,
            color: isSelected ? "primary.light" : (isLeaf ? colors.grey[300] : colors.grey[100]),
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {node.name}
        </Typography>
      </Box>
      {!isLeaf && showChildren && (
        <Box>
          {node.children.map((child) => (
            <ValueTreeNode
              key={child.name}
              node={child}
              depth={depth + 1}
              mibFilename={mibFilename}
              selectedKey={selectedKey}
              onSelect={onSelect}
              isSearching={isSearching}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Tab "MIB" — API di test (TASK-AI-030): un asset può avere più MIB
// caricati in parallelo (uno per prodotto/famiglia di OID, es. il MIB
// generico della stampante + un MIB privato del vendor); ognuno mantiene la
// propria selezione, ma il profilo davvero applicato sul dispositivo è la
// somma delle selezioni di tutti — vedi pushCombinedProfile lato server. La
// visualizzazione ad albero (come un MIB browser, OID accanto a ogni
// oggetto) e la selezione a cascata restano per-MIB nel tab "Configure"; i
// valori estratti nel tab "Values" sono invece condivisi/combinati.
const MibPanel = ({ assetId, snmpLastScan }) => {
  const [mibs, setMibs] = useState([]);
  const [customFields, setCustomFields] = useState({});
  const [selectedMibId, setSelectedMibId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [checkedFields, setCheckedFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("configure");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [valuesFilter, setValuesFilter] = useState("");
  // Gruppi (MIB) chiusi a mano nell'albero del tab "Values" — Set vuoto =
  // tutto aperto di default, un nome vi entra solo quando l'utente clicca
  // per chiuderlo (durante una ricerca i rami restano comunque aperti in
  // automatico, vedi ValueTreeNode/isSearching, indipendentemente da questo
  // Set).
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [tablePages, setTablePages] = useState({});
  // Campo puntato da "Edit in Configure" nel pannello di dettaglio del tab
  // "Values" — il tab Configure lo mostra evidenziato ed espande i rami
  // necessari per rivelarlo.
  const [focusedNodeName, setFocusedNodeName] = useState(null);
  // Campo selezionato nell'albero del tab "Values", di cui il pannello di
  // dettaglio a destra mostra il valore.
  const [selectedValue, setSelectedValue] = useState(null);

  const checkedSet = useMemo(() => new Set(checkedFields), [checkedFields]);
  const nodesByName = useMemo(() => {
    const map = new Map();
    mibs.forEach((mib) => flattenNodes(mib.candidateTree, map));
    return map;
  }, [mibs]);
  const activeMib = mibs.find((m) => m.mibId === selectedMibId) || null;

  // MIB (nome file + id) da cui proviene ogni campo — usato sia per
  // raggruppare il tab "Values" (stile Zabbix "Application"/Lansweeper
  // categorie) invece di un'unica lista piatta senza fine quando sono
  // caricati più MIB, sia per sapere dove saltare quando si clicca un nome
  // per vederlo nell'albero (serve il mibId per selezionare il MIB giusto
  // prima di poterci cercare dentro). Se un nome non è in nessun albero
  // attuale (es. valore rimasto da un MIB nel frattempo eliminato) finisce
  // nel gruppo "Other" e non è cliccabile.
  const fieldMeta = useMemo(() => {
    const map = new Map();
    mibs.forEach((mib) => {
      flattenNodes(mib.candidateTree).forEach((_node, name) => {
        if (!map.has(name)) map.set(name, { filename: mib.filename, mibId: mib.mibId });
      });
    });
    return map;
  }, [mibs]);

  // Antenati da tenere forzatamente espansi per rivelare il nodo puntato da
  // "vedi nell'albero" — solo dentro al MIB attivo, ricalcolato ogni volta
  // che cambiano MIB attivo o target (cambiare MIB da solo non serve,
  // findPathToNode è comunque economico su un albero di poche centinaia di
  // nodi).
  const focusExpandSet = useMemo(() => {
    if (!focusedNodeName || !activeMib) return null;
    const path = findPathToNode(activeMib.candidateTree, focusedNodeName);
    return path ? new Set(path) : null;
  }, [focusedNodeName, activeMib]);

  // "Edit in Configure" nel pannello di dettaglio del tab Values -> tab
  // Configure, MIB giusto selezionato, albero aperto ed evidenziato sul
  // nodo. Nessun effetto se il campo non è (più) in nessun MIB caricato
  // (es. era di un MIB nel frattempo eliminato).
  const goToTree = (fieldName) => {
    const meta = fieldMeta.get(fieldName);
    if (!meta) return;
    setSelectedMibId(meta.mibId);
    setActiveTab("configure");
    setFocusedNodeName(fieldName);
  };

  const toggleGroup = (name) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const loadMibs = () => {
    if (!assetId) return;
    setLoading(true);
    API.getMibsForAsset(assetId)
      .then((res) => {
        const data = res.data?.data ?? { mibs: [], customFields: {} };
        const nextMibs = data.mibs || [];
        setMibs(nextMibs);
        setCustomFields(data.customFields || {});
        setSelectedMibId((current) =>
          current && nextMibs.some((m) => m.mibId === current) ? current : (nextMibs[0]?.mibId ?? null)
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(loadMibs, [assetId]);

  // Ricarica la selezione locale (usata per i checkbox dell'albero) ogni
  // volta che cambia il MIB attivo — resta scollegata da mib.selectedFields
  // finché non si preme "Save selection", per non perdere i toggle in corso
  // ad ogni refresh in background.
  useEffect(() => {
    setCheckedFields(activeMib?.selectedFields || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMibId]);

  // Il filtro può restringere le righe di una tabella (o farla sparire del
  // tutto): riparti sempre da pagina 1 invece di restare su un indice che
  // potrebbe non esistere più col nuovo set di righe.
  useEffect(() => {
    setTablePages({});
  }, [valuesFilter]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) setPendingFile(file);
  };

  const handleUpload = () => {
    if (!pendingFile) return;
    setUploading(true);
    startToast();
    API.uploadMib(assetId, pendingFile)
      .then((res) => {
        const data = res.data?.data;
        updateToastSuccess("MIB uploaded successfully");
        setUploadOpen(false);
        setPendingFile(null);
        setActiveTab("configure");
        if (data?.mibId) setSelectedMibId(data.mibId);
        loadMibs();
      })
      .catch(() => updateToastError("Error uploading MIB"))
      .finally(() => setUploading(false));
  };

  const confirmDeleteMib = () => {
    if (!deleteTarget) return;
    const { mibId } = deleteTarget;
    setDeleteTarget(null);
    startToast();
    API.deleteMib(mibId)
      .then((res) => {
        const { pushed, pushReason, unresolvedFields } = res.data || {};
        const unresolvedNote = describeUnresolved(unresolvedFields);
        if (pushed && !unresolvedNote) {
          updateToastSuccess("MIB removed and applied");
        } else if (pushed) {
          updateToastWarning(`MIB removed and applied${unresolvedNote}`);
        } else {
          updateToastWarning(`MIB removed, not yet applied (${pushReason || "will retry on next scan"})${unresolvedNote}`);
        }
        loadMibs();
      })
      .catch(() => updateToastError("Error removing MIB"));
  };

  // Click su un nodo pienamente selezionato -> deseleziona lui e tutti i
  // discendenti. Click da unchecked/indeterminate -> seleziona a cascata
  // lui e tutti i discendenti (l'utente potrà poi escludere singoli figli
  // ricliccandoli).
  const toggleNode = (node) => {
    const names = collectNames(node);
    setCheckedFields((current) => {
      const currentSet = new Set(current);
      const isFullyChecked = names.every((n) => currentSet.has(n));
      if (isFullyChecked) {
        return current.filter((n) => !names.includes(n));
      }
      names.forEach((n) => currentSet.add(n));
      return Array.from(currentSet);
    });
  };

  // Il salvataggio applica la selezione COMBINATA di tutti i MIB
  // dell'asset (vedi selectMibFields/pushCombinedProfile lato server), non
  // solo quella del MIB attivo — "pushed" riflette se è stata confermata
  // entro il timeout, non solo se il salvataggio su Mongo è riuscito.
  // Nessun dettaglio implementativo (broker/protocollo interno) nel
  // messaggio: per l'utente finale questo passaggio deve restare
  // trasparente.
  const handleSaveSelection = () => {
    if (!activeMib) return;
    setSaving(true);
    startToast();
    API.saveMibFieldSelection(activeMib.mibId, checkedFields)
      .then((res) => {
        const { pushed, pushReason, unresolvedFields } = res.data || {};
        const unresolvedNote = describeUnresolved(unresolvedFields);
        if (pushed && !unresolvedNote) {
          updateToastSuccess("Selection saved and applied");
        } else if (pushed) {
          updateToastWarning(`Selection saved and applied${unresolvedNote}`);
        } else {
          updateToastWarning(`Selection saved, not yet applied (${pushReason || "will retry on next scan"})${unresolvedNote}`);
        }
        loadMibs();
      })
      .catch(() => updateToastError("Error saving field selection"))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <Typography sx={{ color: colors.grey[500], p: 2 }}>Loading MIB…</Typography>;
  }

  const totalFields = countNodes(activeMib?.candidateTree);
  const extractedEntries = Object.entries(customFields);
  const showTabs = mibs.length > 0 || extractedEntries.length > 0;

  // Ricerca globale nel tab "Values" — non solo sui nomi: uno scalare
  // matcha su nome O valore, una tabella matcha sul proprio nome (allora
  // conta come match il nodo stesso) oppure sul contenuto delle celle di
  // almeno una riga.
  const normalizedFilter = valuesFilter.trim().toLowerCase();
  const matchesFilter = (text) => !normalizedFilter || String(text).toLowerCase().includes(normalizedFilter);

  const hasValue = (name) => Object.prototype.hasOwnProperty.call(customFields, name);
  const matchesLeaf = (name) => {
    if (!hasValue(name)) return false;
    if (!normalizedFilter) return true;
    const value = customFields[name];
    if (isRowTable(value)) {
      return matchesFilter(name) || value.some((row) => Object.values(row).some((cell) => matchesFilter(formatCell(cell))));
    }
    return matchesFilter(name) || matchesFilter(formatValue(value));
  };

  // Pota l'albero candidateTree di UN MIB a contenere solo le foglie con un
  // valore estratto (che soddisfano anche la ricerca, se attiva) e i rami
  // necessari per arrivarci — un ramo senza discendenti superstiti sparisce
  // del tutto, non solo si svuota.
  const pruneToValues = (nodes) => {
    const kept = [];
    (nodes || []).forEach((node) => {
      if (matchesLeaf(node.name)) {
        kept.push({ ...node, children: [] });
        return;
      }
      const children = pruneToValues(node.children);
      if (children.length > 0) kept.push({ ...node, children });
    });
    return kept;
  };

  const countLeaves = (nodes) => nodes.reduce((sum, n) => sum + (n.children.length === 0 ? 1 : countLeaves(n.children)), 0);

  const valueTreesByMib = mibs
    .map((mib) => {
      const tree = pruneToValues(mib.candidateTree);
      return tree.length > 0 ? { mib, tree, leafCount: countLeaves(tree) } : null;
    })
    .filter(Boolean);

  // Elenco piatto delle foglie superstiti, nell'ordine dell'albero — usato
  // per il conteggio dei match e per ricadere sulla prima foglia disponibile
  // quando la selezione corrente non esiste più (MIB eliminato, filtro
  // cambiato, ecc.), senza bisogno di un effect dedicato.
  const flatLeaves = [];
  valueTreesByMib.forEach(({ mib, tree }) => {
    const walk = (nodes) => nodes.forEach((n) => (n.children.length === 0 ? flatLeaves.push({ mibFilename: mib.filename, node: n }) : walk(n.children)));
    walk(tree);
  });
  const displayedSelection =
    selectedValue && flatLeaves.some((l) => l.mibFilename === selectedValue.mibFilename && l.node.name === selectedValue.node.name)
      ? selectedValue
      : flatLeaves[0] || null;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.grey[100] }}>
            {mibs.length ? `${mibs.length} MIB${mibs.length === 1 ? "" : "s"} uploaded` : "No MIB uploaded for this asset"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: colors.grey[500], mt: 0.25 }}>
            Last SNMP scan: {snmpLastScan ? new Date(snmpLastScan).toLocaleString() : "—"}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => setUploadOpen(true)}
        >
          Add MIB
        </Button>
      </Box>

      {mibs.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {mibs.map((mib) => {
            const selectedCount = mib.mibId === selectedMibId ? checkedFields.length : (mib.selectedFields?.length || 0);
            return (
              <Chip
                key={mib.mibId}
                label={`${mib.filename} · ${selectedCount} selected`}
                clickable
                onClick={() => { setSelectedMibId(mib.mibId); setActiveTab("configure"); setFocusedNodeName(null); }}
                onDelete={() => setDeleteTarget({ mibId: mib.mibId, filename: mib.filename })}
                color={mib.mibId === selectedMibId ? "primary" : "default"}
                variant={mib.mibId === selectedMibId ? "filled" : "outlined"}
                size="small"
              />
            );
          })}
        </Box>
      )}

      {showTabs && (
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          sx={{ minHeight: 36, mb: 1.5, borderBottom: `1px solid ${colors.grey[800]}` }}
        >
          <Tab value="configure" label="Configure" sx={{ minHeight: 36, fontSize: 12.5 }} />
          <Tab
            value="values"
            label={extractedEntries.length ? `Values (${extractedEntries.length})` : "Values"}
            sx={{ minHeight: 36, fontSize: 12.5 }}
          />
        </Tabs>
      )}

      {activeTab === "configure" && activeMib && totalFields > 0 && (
        <Box sx={{ borderRadius: 1.5, border: `1px solid ${colors.grey[800]}`, p: 1.5, overflowX: "auto" }}>
          <Typography sx={{ fontSize: 12, color: colors.grey[400], mb: 1 }}>
            Select the fields of interest for this MIB — selecting a node selects all its children too, uncheck any to exclude them
          </Typography>
          <Box sx={{ minWidth: 480 }}>
            {activeMib.candidateTree.map((node) => (
              <MibTreeNode
                key={node.name}
                node={node}
                checkedSet={checkedSet}
                onToggle={toggleNode}
                forceExpandSet={focusExpandSet}
                highlightName={focusedNodeName}
              />
            ))}
          </Box>
          <Button
            variant="contained"
            size="small"
            sx={{ mt: 1.5 }}
            disabled={saving}
            onClick={handleSaveSelection}
          >
            Save selection
          </Button>
        </Box>
      )}

      {activeTab === "configure" && mibs.length === 0 && (
        <Box sx={{ borderRadius: 1.5, border: `1px solid ${colors.grey[800]}`, p: 1.5 }}>
          <Typography sx={{ fontSize: 12.5, color: colors.grey[500] }}>
            Upload a MIB to select which fields to collect from this device.
          </Typography>
        </Box>
      )}

      {activeTab === "values" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {extractedEntries.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                size="small"
                placeholder="Search field name or value…"
                value={valuesFilter}
                onChange={(e) => setValuesFilter(e.target.value)}
                sx={{ maxWidth: 340 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: colors.grey[500] }} />
                    </InputAdornment>
                  ),
                }}
              />
              {normalizedFilter && (
                <Typography sx={{ fontSize: 11, color: colors.grey[500] }}>
                  {flatLeaves.length} match{flatLeaves.length === 1 ? "" : "es"}
                </Typography>
              )}
            </Box>
          )}

          {extractedEntries.length === 0 ? (
            <Box sx={{ borderRadius: 1.5, border: `1px solid ${colors.grey[800]}`, p: 1.5 }}>
              <Typography sx={{ fontSize: 12.5, color: colors.grey[500], py: 1 }}>
                No values yet — waiting for the next completed scan.
              </Typography>
            </Box>
          ) : valueTreesByMib.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: colors.grey[500], py: 1 }}>
              No values match "{valuesFilter}".
            </Typography>
          ) : (
            // Master-detail: a sinistra l'albero dei campi con un valore
            // estratto, con la stessa struttura del MIB (non una lista
            // piatta); a destra il dettaglio del campo selezionato, con
            // scroll indipendente — invece di mostrare tutto insieme, che
            // con centinaia di campi degenera in uno scroll infinito senza
            // punti di riferimento.
            <Box sx={{ display: "flex", border: `1px solid ${colors.grey[800]}`, borderRadius: 1.5, overflow: "hidden", height: 560 }}>
              <Box sx={{ width: 240, flexShrink: 0, borderRight: `1px solid ${colors.grey[800]}`, overflowY: "auto", py: 1 }}>
                {valueTreesByMib.map(({ mib, tree, leafCount }) => {
                  const expanded = !!normalizedFilter || !collapsedGroups.has(mib.filename);
                  return (
                    <Box key={mib.filename} sx={{ mb: 0.25 }}>
                      <Box
                        onClick={() => toggleGroup(mib.filename)}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.75, cursor: "pointer", "&:hover": { bgcolor: "#ffffff08" } }}
                      >
                        {expanded ? <ExpandMoreIcon sx={{ fontSize: 14, color: colors.grey[500] }} /> : <ChevronRightIcon sx={{ fontSize: 14, color: colors.grey[500] }} />}
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.grey[100], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                          {mib.filename}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: colors.grey[500], bgcolor: colors.grey[800], borderRadius: 0.5, px: 0.6 }}>
                          {leafCount}
                        </Typography>
                      </Box>
                      {expanded && (
                        <Box>
                          {tree.map((node) => (
                            <ValueTreeNode
                              key={node.name}
                              node={node}
                              mibFilename={mib.filename}
                              selectedKey={displayedSelection ? valueKey(displayedSelection.mibFilename, displayedSelection.node.name) : null}
                              onSelect={(mibFilename, n) => setSelectedValue({ mibFilename, node: n })}
                              isSearching={!!normalizedFilter}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", p: 2 }}>
                {!displayedSelection ? (
                  <Typography sx={{ fontSize: 12.5, color: colors.grey[500] }}>
                    Select a field from the tree to see its value.
                  </Typography>
                ) : (() => {
                  const { node } = displayedSelection;
                  const value = customFields[node.name];
                  const canEditInConfigure = fieldMeta.has(node.name);

                  const header = (
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: colors.grey[100], wordBreak: "break-word" }}>
                          {node.name}
                        </Typography>
                        {node.oid && (
                          <Typography sx={{ fontSize: 10.5, color: colors.grey[500], fontFamily: "monospace", mt: 0.25 }}>
                            {node.oid}
                          </Typography>
                        )}
                      </Box>
                      {canEditInConfigure && (
                        <Typography
                          onClick={() => goToTree(node.name)}
                          sx={{
                            fontSize: 11, fontWeight: 600, color: colors.grey[400], whiteSpace: "nowrap", cursor: "pointer",
                            "&:hover": { color: LINK_HOVER_COLOR, textDecoration: "underline" },
                          }}
                        >
                          Edit in Configure
                        </Typography>
                      )}
                    </Box>
                  );

                  if (!isRowTable(value)) {
                    return (
                      <Box>
                        {header}
                        <Typography sx={{ fontSize: 16, color: colors.grey[100], fontFamily: "monospace", wordBreak: "break-word", maxWidth: 680, lineHeight: 1.5 }}>
                          {formatValue(value)}
                        </Typography>
                        {node.description && (
                          <Typography sx={{ fontSize: 12, color: colors.grey[400], mt: 2, maxWidth: 680, lineHeight: 1.6 }}>
                            {node.description}
                          </Typography>
                        )}
                      </Box>
                    );
                  }

                  // Se il filtro non matcha il nome della tabella stessa, ma
                  // è per questo che è comparsa nell'albero, allora matcha
                  // almeno una riga: mostriamo solo quelle, coerente con
                  // come la ricerca ha portato l'utente qui.
                  const rows = !normalizedFilter || matchesFilter(node.name)
                    ? value
                    : value.filter((row) => Object.values(row).some((cell) => matchesFilter(formatCell(cell))));
                  const columns = Array.from(rows.reduce((set, row) => {
                    Object.keys(row).forEach((k) => set.add(k));
                    return set;
                  }, new Set()));
                  const pageCount = Math.max(1, Math.ceil(rows.length / TABLE_ROWS_PER_PAGE));
                  const page = Math.min(tablePages[node.name] || 0, pageCount - 1);
                  const pageRows = rows.slice(page * TABLE_ROWS_PER_PAGE, (page + 1) * TABLE_ROWS_PER_PAGE);

                  return (
                    <Box>
                      {header}
                      <Typography sx={{ fontSize: 11, color: colors.grey[500], mb: 1.5 }}>
                        {rows.length === value.length
                          ? `${value.length} row${value.length === 1 ? "" : "s"}`
                          : `${rows.length} of ${value.length} rows matching "${valuesFilter}"`}
                      </Typography>
                      {/* Paginata (non scroll ad altezza fissa): una tabella con
                          centinaia di righe (es. software installato) resta
                          leggibile invece di diventare un pozzo senza fondo. */}
                      <Box sx={{ overflowX: "auto" }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${columns.length}, max-content)`,
                            columnGap: 3,
                            rowGap: 0.5,
                          }}
                        >
                          {columns.map((col) => (
                            <Tooltip key={col} title={nodesByName.get(col)?.description || ""} arrow placement="top">
                              <Typography
                                sx={{
                                  fontSize: 11, fontWeight: 700, color: colors.grey[400], whiteSpace: "nowrap",
                                  pb: 0.5, px: 0.5, borderBottom: `1px solid ${colors.grey[800]}`,
                                  maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis",
                                }}
                              >
                                {col}
                              </Typography>
                            </Tooltip>
                          ))}
                          {pageRows.map((row, i) => columns.map((col) => {
                            const cellText = formatCell(row[col]);
                            return (
                              // maxWidth qui limita anche la larghezza della colonna
                              // nel grid (max-content ne tiene conto): valori lunghi
                              // (es. nomi software) si troncano con "…" invece di
                              // uscire dalla viewport e richiedere scroll orizzontale
                              // — il valore completo resta leggibile in tooltip. Sfondo
                              // alternato per riga (zebra), per scandire le tabelle con
                              // molte colonne/righe.
                              <Tooltip key={`${i}-${col}`} title={cellText} arrow placement="top">
                                <Typography
                                  sx={{
                                    fontSize: 12, color: colors.grey[100], whiteSpace: "nowrap",
                                    maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis",
                                    px: 0.5, py: 0.15, borderRadius: 0.5,
                                    bgcolor: i % 2 ? ROW_STRIPE : "transparent",
                                  }}
                                >
                                  {cellText}
                                </Typography>
                              </Tooltip>
                            );
                          }))}
                        </Box>
                      </Box>
                      {pageCount > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                          <Pagination
                            size="small"
                            count={pageCount}
                            page={page + 1}
                            onChange={(_e, val) => setTablePages((current) => ({ ...current, [node.name]: val - 1 }))}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload MIB</DialogTitle>
        <DialogContent>
          <Box
            component="label"
            sx={{
              mt: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: 140,
              border: "2px dashed",
              borderColor: "divider",
              borderRadius: 2,
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
            }}
          >
            <input type="file" hidden accept=".mib,.txt,text/plain" onChange={handleFileSelect} />
            <CloudUploadIcon sx={{ fontSize: 36, color: "text.secondary", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {pendingFile ? pendingFile.name : "Click to select a MIB file (.mib, .txt)"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Close</Button>
          <Button variant="contained" disabled={!pendingFile || uploading} onClick={handleUpload}>
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove MIB</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Remove "{deleteTarget?.filename}"? Fields selected from it will stop being collected on this asset.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteMib}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MibPanel;
